import { useState, useMemo, useEffect, useRef } from 'react';
import ReactApexChart from 'react-apexcharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { ko } from 'date-fns/locale';
import styled, { useTheme } from 'styled-components';
import { CONTENT_TYPE_MAP, TContentType, ITrendParams } from 'type/tracking';

import { useGetTrackingTrend } from 'hooks/queries/tracking';
import CoreGrayButton from 'shared/button/CoreGrayButton';
import CoreRadioChips from 'shared/chips/CoreRadioChips';
import CoreLoading from 'shared/CoreLoading';
import CoreSvg from 'shared/CoreSvg';
import Icon from 'shared/icons';
import {
	ReportSection,
	ReportSectionCard,
	ReportSectionHeader,
} from 'styles/styled-components/report';
import style from 'styles/styled-components/style';

interface ITrendChart {
	code: string;
	onFilterChange?: (params: ITrendParams) => void;
	onBarClick?: (date: string | null) => void;
	selectedDate?: string | null;
	trackingStartDate?: string;
	trackingEndDate?: string;
}

type MetricType = 'VIEW' | 'LIKE' | 'COMMENT' | 'SHARE';
type AggregationType = 'DAILY' | 'CUMULATIVE';

const METRIC_OPTIONS = ['조회수', '좋아요', '댓글', '공유'];
const METRIC_MAP: Record<string, MetricType> = {
	조회수: 'VIEW',
	좋아요: 'LIKE',
	댓글: 'COMMENT',
	공유: 'SHARE',
};

const AGGREGATION_OPTIONS = ['일별 집계', '누적 집계'];
const AGGREGATION_MAP: Record<string, AggregationType> = {
	'일별 집계': 'DAILY',
	'누적 집계': 'CUMULATIVE',
};

const TRACKING_CONTENT_TYPES: TContentType[] = [
	'ig_feed',
	'ig_video',
	'yt_video',
	'yt_shorts',
];
const CONTENT_TYPE_OPTIONS = TRACKING_CONTENT_TYPES.map((key) => CONTENT_TYPE_MAP[key]);
const CONTENT_TYPE_REVERSE_MAP: Record<string, TContentType> = {};
TRACKING_CONTENT_TYPES.forEach((key) => {
	CONTENT_TYPE_REVERSE_MAP[CONTENT_TYPE_MAP[key]] = key;
});

const TrendChart = ({ code, onFilterChange, onBarClick, selectedDate, trackingStartDate, trackingEndDate }: ITrendChart) => {
	const [selectedMetric, setSelectedMetric] = useState('조회수');
	const [selectedAggregation, setSelectedAggregation] = useState('일별 집계');
	const [selectedContentTypes, setSelectedContentTypes] = useState<TContentType[]>([...TRACKING_CONTENT_TYPES]);
	const [startDate, setStartDate] = useState<Date | null>(null);
	const [endDate, setEndDate] = useState<Date | null>(null);
	const theme = useTheme();

	const minDate = trackingStartDate ? new Date(trackingStartDate) : undefined;
	const maxDate = trackingEndDate ? new Date(trackingEndDate) : undefined;

	const formatDateStr = (d: Date | null) => {
		if (!d) return '';
		const yyyy = d.getFullYear();
		const mm = String(d.getMonth() + 1).padStart(2, '0');
		const dd = String(d.getDate()).padStart(2, '0');
		return `${yyyy}-${mm}-${dd}`;
	};

	const trendParams: ITrendParams = useMemo(() => {
		const params: ITrendParams = {
			metric: METRIC_MAP[selectedMetric],
			aggregationType: AGGREGATION_MAP[selectedAggregation],
			contentTypes:
				selectedContentTypes.length === 0 || selectedContentTypes.length === TRACKING_CONTENT_TYPES.length
					? TRACKING_CONTENT_TYPES
					: selectedContentTypes,
		};
		const startStr = formatDateStr(startDate);
		const endStr = formatDateStr(endDate);
		if (startStr) params.startDate = startStr;
		if (endStr) params.endDate = endStr;
		return params;
	}, [selectedMetric, selectedAggregation, selectedContentTypes, startDate, endDate]);

	const { data: trendData, isLoading } = useGetTrackingTrend(code, trendParams);

	// onFilterChange 콜백 + 필터 변경 시 날짜 선택 초기화
	useEffect(() => {
		onFilterChange?.(trendParams);
		onBarClick?.(null);
	}, [trendParams]);

	// selectedDate와 trendData에서 인덱스를 파생 (별도 state 없이 항상 동기화)
	const selectedDateIndex = useMemo(() => {
		if (!selectedDate || !trendData) return null;
		const idx = trendData.findIndex((item) => item.date === selectedDate);
		return idx >= 0 ? idx : null;
	}, [selectedDate, trendData]);

	// 차트 클릭 핸들러 (바/포인트 클릭 시 해당 날짜 선택)
	const handleChartClick = (_event: MouseEvent, _chartContext: unknown, config: { dataPointIndex: number }) => {
		const idx = config.dataPointIndex;
		if (idx < 0 || !trendData?.[idx]) return;
		const clickedDate = trendData[idx].date;
		if (selectedDate === clickedDate) {
			// 같은 바/포인트 재클릭 시 선택 해제
			onBarClick?.(null);
		} else {
			onBarClick?.(clickedDate);
		}
	};

	const isArea = selectedAggregation === '누적 집계';

	const chartOptions: ApexCharts.ApexOptions = useMemo(() => {
		return {
			chart: {
				type: isArea ? 'area' : 'bar',
				toolbar: { show: false },
				zoom: { enabled: false },
				events: {
					click: handleChartClick,
					// area 차트에서는 markerClick도 등록 (마커 직접 클릭 시)
					...(isArea
						? {
								markerClick: (
									_event: MouseEvent,
									_chartContext: unknown,
									config: { dataPointIndex: number },
								) => handleChartClick(_event, _chartContext, config),
						  }
						: {}),
				},
			},
			dataLabels: { enabled: false },
			stroke: {
				curve: 'smooth',
				width: isArea ? 2 : 0,
				colors: isArea ? [theme.colors.uplus.primary50_key] : undefined,
			},
			fill: {
				type: isArea ? 'gradient' : 'solid',
				gradient: {
					shadeIntensity: 1,
					opacityFrom: 0.4,
					opacityTo: 0.1,
				},
			},
			// area 차트: 기본 마커 숨기고, 선택된 포인트만 discrete로 표시
			markers: {
				size: 0,
				hover: { sizeOffset: isArea ? 4 : 0 },
				discrete:
					isArea && selectedDateIndex !== null && trendData?.length
						? [
								{
									seriesIndex: 0,
									dataPointIndex: selectedDateIndex,
									fillColor: theme.colors.uplus.primary50_key,
									strokeColor: '#fff',
									size: 7,
								},
						  ]
						: [],
			},
			xaxis: {
				categories:
					trendData?.map((item) => {
						const d = item.date ?? '';
						const parts = d.split('-');
						return parts.length >= 3 ? `${parts[1]}-${parts[2]}` : d;
					}) ?? [],
				tickAmount: Math.min(trendData?.length ?? 0, 15),
				labels: {
					style: {
						fontSize: '11px',
						colors: theme.colors.gray.gray30,
					},
					rotate: 0,
					hideOverlappingLabels: true,
				},
				axisBorder: { show: true, color: theme.colors.gray.gray15 },
				axisTicks: { show: false },
				crosshairs: {
					show: true,
					width: 1,
					stroke: { color: theme.colors.gray.gray30, width: 1, dashArray: 3 },
				},
			},
			yaxis: {
				opposite: true,
				labels: {
					style: {
						fontSize: '11px',
						colors: theme.colors.gray.gray30,
					},
					formatter: (val: number) => (val ?? 0).toLocaleString(),
				},
				axisBorder: { show: true, color: theme.colors.gray.gray15 },
				axisTicks: { show: false },
			},
			tooltip: {
				intersect: false,
				shared: true,
				custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
					const item = trendData?.[dataPointIndex];
					if (!item) return '';
					const metricLabel =
						selectedMetric === '조회수'
							? '조회 수'
							: selectedMetric === '좋아요'
							? '좋아요 수'
							: selectedMetric === '댓글'
							? '댓글 수'
							: '공유 수';
					const rate = item.changeRate;
					const trendingLine = item.trendingContentCount
						? `<div style="color: #FF9800; font-weight: 500;">⚡ 급상승 콘텐츠 수 : ${item.trendingContentCount}</div>`
						: `<div style="color: #CED4DA;">급상승 콘텐츠 수 : 0</div>`;
					if (rate == null) {
						return `
							<div style="padding: 12px 16px; font-size: 13px; line-height: 1.8; min-width: 200px;">
								<div style="font-weight: 600; margin-bottom: 2px;">${item.date}</div>
								<div>${metricLabel}: ${(item.value ?? 0).toLocaleString()}</div>
								<div>집계 콘텐츠 수: ${item.contentCount} (신규:${item.newContentCount})</div>
								<div style="color: #868E96;">변동률 정보 없음</div>
								${trendingLine}
							</div>
						`;
					}
					const rateAbs = Math.abs(rate).toFixed(1);
					const rateColor = rate > 0 ? '#FF5452' : rate < 0 ? '#458EFF' : '#868E96';
					const rateText =
						rate > 0 ? `${rateAbs}% 상승▲` : rate < 0 ? `${rateAbs}% 하락▼` : '변동 없음';
					return `
						<div style="padding: 12px 16px; font-size: 13px; line-height: 1.8; min-width: 200px;">
							<div style="font-weight: 600; margin-bottom: 2px;">${item.date}</div>
							<div>${metricLabel}: ${(item.value ?? 0).toLocaleString()}</div>
							<div>집계 콘텐츠 수: ${item.contentCount} (신규:${item.newContentCount})</div>
							<div style="color: ${rateColor};">전일 대비 ${rateText}</div>
							${trendingLine}
						</div>
					`;
				},
			},
			colors: [theme.colors.uplus.primary50_key],
			grid: {
				show: false,
			},
			plotOptions: {
				bar: {
					borderRadius: 4,
					columnWidth: '60%',
					// 날짜 선택 시 개별 바 색상을 fillColor로 제어하기 위해 distributed 활성화
					distributed: selectedDateIndex !== null && !isArea,
				},
			},
			// distributed 모드에서 자동 생성되는 범례 숨김
			legend: {
				show: false,
			},
		};
	}, [trendData, selectedMetric, isArea, theme, selectedDateIndex]);

	const chartSeries = useMemo(() => {
		// 바 차트에서 날짜가 선택된 경우 선택된 바만 primary 색상, 나머지는 연한 색상
		const isBarSelected = selectedDateIndex !== null && !isArea;
		type DataPoint = { x: string; y: number; fillColor: string };
		const data: DataPoint[] | number[] = isBarSelected
			? (trendData?.map((item, idx) => ({
					x: item.date,
					y: item.value,
					fillColor:
						idx === selectedDateIndex
							? theme.colors.uplus.primary50_key
							: `${theme.colors.uplus.primary50_key}40`,
			  })) ?? [])
			: (trendData?.map((item) => item.value) ?? []);
		return [
			{
				name: selectedMetric,
				data,
			},
		];
	}, [trendData, selectedMetric, selectedDateIndex, isArea, theme]);

	return (
		<ReportSection _margin="0">
			<Grid flex="between" align_items="center" _margin="0 0 8px 0">
				<ReportSectionHeader _margin="0">콘텐츠 성과 추이</ReportSectionHeader>
				<CoreGrayButton
					size="default"
					style_type="filled"
					text="콘텐츠별 상세 트래킹 바로가기(피처링) ↗"
					_onClick={() =>
						window.open(`https://app.featuring.co/content-tracking/${code}`, '_blank', 'noopener,noreferrer')
					}
				/>
			</Grid>
			<Text typo="p" color="gray" _margin="0 0 16px 0">
				참여 크리에이터들이 수집 기간 내 발행한 콘텐츠의 일별 성과 데이터를 확인할 수
				있습니다.
			</Text>

			<ReportSectionCard _padding="24px" _height="auto">
				<FilterRow>
					<FilterGroup>
						<DatePickerWrapper>
							<DatePicker
								selectsRange
								startDate={startDate}
								endDate={endDate}
								onChange={(dates: [Date | null, Date | null]) => {
									const [start, end] = dates;
									setStartDate(start);
									setEndDate(end);
								}}
								dateFormat="yyyy.MM.dd"
								placeholderText="기간 선택"
								locale={ko}
								isClearable
								minDate={minDate}
								maxDate={maxDate}
							/>
						</DatePickerWrapper>
					</FilterGroup>
					<FilterGroup>
						<ContentTypeSelect
							selected={selectedContentTypes}
							onChange={setSelectedContentTypes}
						/>
					</FilterGroup>
					<FilterSpacer />
					<FilterGroup>
						<CoreRadioChips
							group_type="carousel"
							style_type="gray"
							_width="auto"
							chips_list={AGGREGATION_OPTIONS}
							selected_chips={selectedAggregation}
							setSelectedChips={setSelectedAggregation}
						/>
					</FilterGroup>
					<FilterGroup>
						<CoreRadioChips
							group_type="carousel"
							style_type="gray"
							_width="auto"
							chips_list={METRIC_OPTIONS}
							selected_chips={selectedMetric}
							setSelectedChips={setSelectedMetric}
						/>
					</FilterGroup>
				</FilterRow>

				{isLoading ? (
					<Grid flex="center" _height="365px">
						<CoreLoading color_type="black" size_type="large" />
					</Grid>
				) : !trendData?.length ? (
					<Grid flex="center" _height="365px">
						<Text typo="p" color="gray">
							데이터가 없습니다.
						</Text>
					</Grid>
				) : (
					<ChartWrapper>
						<ReactApexChart
							options={chartOptions}
							series={chartSeries}
							type={isArea ? 'area' : 'bar'}
							height={350}
						/>
					</ChartWrapper>
				)}
			</ReportSectionCard>
		</ReportSection>
	);
};

// ====== 콘텐츠 유형 멀티셀렉트 드롭다운 ======
const CONTENT_TYPE_ICON: Record<string, JSX.Element> = {
	ig_feed: <Icon.Insta />,
	ig_video: <Icon.InstaReels />,
	yt_video: <Icon.Youtube />,
	yt_shorts: <Icon.YoutubeShorts />,
};

const ContentTypeSelect = ({
	selected,
	onChange,
}: {
	selected: TContentType[];
	onChange: (types: TContentType[]) => void;
}) => {
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const allSelected = selected.length === TRACKING_CONTENT_TYPES.length || selected.length === 0;

	const toggleAll = () => {
		if (allSelected) {
			onChange([]);
		} else {
			onChange([...TRACKING_CONTENT_TYPES]);
		}
	};

	const toggleOne = (type: TContentType) => {
		if (selected.includes(type)) {
			onChange(selected.filter((t) => t !== type));
		} else {
			onChange([...selected, type]);
		}
	};

	const label =
		selected.length === 0 || allSelected
			? '콘텐츠 유형 전체'
			: `콘텐츠 유형 ${selected.length}개`;

	return (
		<CTDropdownWrapper ref={ref}>
			<CTDropdownTrigger $active={open} onClick={() => setOpen(!open)}>
				<span>{label}</span>
				<CTDropdownIconWrap $open={open}>
					<CoreSvg _size="16px">
						<Icon.ArrowDown />
					</CoreSvg>
				</CTDropdownIconWrap>
			</CTDropdownTrigger>
			{open && (
				<CTDropdownPanel>
					<CTOptionList>
						<CTOptionItem onClick={toggleAll}>
							<CTCheckbox $checked={allSelected} $primary={false} />
							<span>전체 선택</span>
						</CTOptionItem>
						{TRACKING_CONTENT_TYPES.map((type) => {
							const checked = selected.includes(type);
							return (
								<CTOptionItem key={type} onClick={() => toggleOne(type)}>
									<CTCheckbox $checked={checked} $primary />
									{CONTENT_TYPE_ICON[type] && (
										<CoreSvg _size="16px">{CONTENT_TYPE_ICON[type]}</CoreSvg>
									)}
									<span>{CONTENT_TYPE_MAP[type]}</span>
								</CTOptionItem>
							);
						})}
					</CTOptionList>
					<CTDropdownFooter>
						<CTCloseBtn onClick={() => setOpen(false)}>닫기</CTCloseBtn>
					</CTDropdownFooter>
				</CTDropdownPanel>
			)}
		</CTDropdownWrapper>
	);
};

const CTDropdownWrapper = styled.div`
	position: relative;
`;

const CTDropdownTrigger = styled.button<{ $active: boolean }>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 4px;
	height: 36px;
	min-width: 160px;
	padding: 0 12px;
	border: ${({ $active, theme }) =>
		$active
			? `2px solid ${theme.colors.uplus.primary50_key}`
			: `1px solid ${theme.colors.gray.gray15}`};
	border-radius: 8px;
	background: ${({ theme }) => theme.colors.gray.gray_white};
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray80_text};
	cursor: pointer;
	white-space: nowrap;
`;

const CTDropdownIconWrap = styled.span<{ $open: boolean }>`
	display: flex;
	align-items: center;
	transition: transform 0.15s;
	transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
`;

const CTDropdownPanel = styled.div`
	position: absolute;
	top: calc(100% + 4px);
	left: 0;
	width: 240px;
	background: ${({ theme }) => theme.colors.gray.gray_white};
	border: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	border-radius: 8px;
	box-shadow: ${({ theme }) => theme.shadow.depth02};
	z-index: 100;
	display: flex;
	flex-direction: column;
`;

const CTOptionList = styled.div`
	max-height: 240px;
	overflow-y: auto;
`;

const CTOptionItem = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	cursor: pointer;
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray80_text};

	&:hover {
		background-color: ${({ theme }) => theme.colors.gray.gray05_bg};
	}
`;

const CTCheckbox = styled.div<{
	$checked: boolean;
	$primary: boolean;
}>`
	width: 18px;
	height: 18px;
	border-radius: 4px;
	border: 1.5px solid
		${({ $checked, $primary, theme }) =>
			$checked && $primary
				? theme.colors.uplus.primary50_key
				: $checked
				? theme.colors.gray.gray50
				: theme.colors.gray.gray30};
	background-color: ${({ $checked, $primary, theme }) =>
		$checked && $primary
			? theme.colors.uplus.primary50_key
			: $checked
			? theme.colors.gray.gray50
			: 'transparent'};
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;

	&::after {
		content: ${({ $checked }) => ($checked ? "'✓'" : "''")};
		color: #fff;
		font-size: 11px;
		font-weight: 700;
	}
`;

const CTDropdownFooter = styled.div`
	display: flex;
	justify-content: flex-end;
	padding: 8px 12px;
	border-top: 1px solid ${({ theme }) => theme.colors.gray.gray15};
`;

const CTCloseBtn = styled.button`
	border: none;
	background: none;
	${({ theme }) => theme.typo.body.span};
	color: ${({ theme }) => theme.colors.gray.gray50};
	cursor: pointer;

	&:hover {
		color: ${({ theme }) => theme.colors.gray.gray80_text};
	}
`;

const FilterRow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 24px;
`;

const FilterSpacer = styled.div`
	flex: 1;
`;

const FilterGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex-shrink: 0;
`;

const DatePickerWrapper = styled.div`
	.react-datepicker-wrapper {
		width: 220px;
	}

	.react-datepicker__input-container input {
		width: 100%;
		padding: 6px 10px;
		border: 1px solid ${({ theme }) => theme.colors.gray.gray15};
		border-radius: 6px;
		${({ theme }) => theme.typo.body.span};
		color: ${({ theme }) => theme.colors.gray.gray80_text};
		outline: none;
		background: transparent;

		&:focus {
			border-color: ${({ theme }) => theme.colors.uplus.primary50_key};
		}

		&::placeholder {
			color: ${({ theme }) => theme.colors.gray.gray30};
		}
	}

	.react-datepicker__close-icon::after {
		background-color: ${({ theme }) => theme.colors.gray.gray30};
		font-size: 12px;
	}

	.react-datepicker {
		border: 1px solid ${({ theme }) => theme.colors.gray.gray15};
		box-shadow: ${({ theme }) => theme.shadow.depth02};
		font-family: inherit;
	}

	.react-datepicker__header {
		background-color: ${({ theme }) => theme.colors.gray.gray05_bg};
		border-bottom: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	}

	.react-datepicker__day--selected,
	.react-datepicker__day--range-start,
	.react-datepicker__day--range-end,
	.react-datepicker__day--keyboard-selected {
		background-color: ${({ theme }) => theme.colors.uplus.primary50_key};
		color: #fff;

		&:hover {
			background-color: ${({ theme }) => theme.colors.uplus.primary50_key};
			opacity: 0.85;
		}
	}

	.react-datepicker__day:hover {
		background-color: ${({ theme }) => theme.colors.uplus.primary80_darken};
		color: #fff;
	}

	.react-datepicker__day--in-range,
	.react-datepicker__day--in-selecting-range {
		background-color: ${({ theme }) => theme.colors.uplus.primary50_key};
		color: #fff;

		&:hover {
			background-color: ${({ theme }) => theme.colors.uplus.primary80_darken};
			color: #fff;
		}
	}

	.react-datepicker__day--in-selecting-range:not(.react-datepicker__day--in-range) {
		background-color: ${({ theme }) => theme.colors.uplus.primary50_key};
		color: #fff;
	}
`;

const ChartWrapper = styled.div`
	width: 100%;
`;

const { Grid, Text } = style;

export default TrendChart;
